/**
 * Carries a signed-out fan's visit source (UTM / QR / referrer) across the
 * magic-link sign-in, which opens in a new tab and loses tab-only storage.
 * Stored in this browser only, no personal data, bound to one Follow intent by a
 * random nonce, single-use, and expires automatically.
 */
import type { Attribution } from "@/lib/analyticsTracker";

export const FOLLOW_ATTR_KEY = "siis_a_follow_attr";
export const FOLLOW_ATTR_TTL_MS = 60 * 60 * 1000; // matches the sign-in link lifetime

type Stash = { nonce: string; athleteId: string; attr: Attribution; expires: number };

const parse = (raw: string | null): Stash | null => {
  try {
    const v = raw ? (JSON.parse(raw) as Partial<Stash>) : null;
    if (!v || typeof v.nonce !== "string" || typeof v.athleteId !== "string" || typeof v.expires !== "number" || !v.attr) return null;
    return v as Stash;
  } catch { return null; }
};

/** Always replaces any earlier stash, so an abandoned intent can never be reused. */
export const stashFollowAttribution = (
  store: Storage, nonce: string, athleteId: string, attr: Attribution, allowed: boolean, now = Date.now(),
) => {
  store.removeItem(FOLLOW_ATTR_KEY);
  if (!allowed || !Object.keys(attr).length) return;
  store.setItem(FOLLOW_ATTR_KEY, JSON.stringify({ nonce, athleteId, attr, expires: now + FOLLOW_ATTR_TTL_MS }));
};

/** Single-use: removed on every read. Returns only for the same intent, athlete and within TTL. */
export const takeFollowAttribution = (
  store: Storage, nonce: string | undefined, athleteId: string, now = Date.now(),
): Attribution | null => {
  const s = parse(store.getItem(FOLLOW_ATTR_KEY));
  store.removeItem(FOLLOW_ATTR_KEY);
  if (!s || !nonce || s.nonce !== nonce || s.athleteId !== athleteId || now > s.expires) return null;
  return s.attr;
};

export const purgeExpiredFollowAttribution = (store: Storage, now = Date.now()) => {
  const raw = store.getItem(FOLLOW_ATTR_KEY);
  if (raw === null) return;
  const s = parse(raw);
  if (!s || now > s.expires) store.removeItem(FOLLOW_ATTR_KEY);
};
